import React, {ReactElement} from "react";
import {Button, ButtonBase, Typography} from "@mui/material";
import {StyledBoxColumn, StyledBoxRow} from "../../../shared/StyledComponents";
import {DIVIDER_XXBLACK, DW_SM, PD_LG, PD_MD} from "../../../shared/dimens";
import {
  findEmptyIndex,
  findSwapIndices,
  SlidleDifficultyLevel,
  SlidleGameBoard,
  SlidleGameConfig,
  SlidleGameData,
  validMoveIndex
} from "./types";
import {BaseApp, DIALOG_FLAG_SHOW_CLOSE} from "../../../shared/BaseApp";
import {GameFragment, GameFragmentProps, GameFragmentState} from "../GameFragment";
import {HelpOutlineOutlined, MoreHorizOutlined} from "@mui/icons-material";

import {GAME_SLIDLE} from "../../Games";
import {Game} from "../../types";
import {Action} from "../../../shared/types";
import {StringUtil} from "../../../shared/string_util";
import {$KTS} from "../../../shared/keytext";
import {GameOverResult, GameOverResultType} from "../types";
import {Throttler} from "../../../shared/file_util";

export type SlidleGameFragmentProps = GameFragmentProps & {}

enum SlidleGameStatus {
  STATUS_IN_PROGRESS,
  STATUS_INCOMPLETE, // Finished, but NOT solved
  STATUS_COMPLETE, // Finished, and solved
}

type SlidleGameFragmentState = GameFragmentState<SlidleGameConfig, SlidleGameData> & {
  gameStatus: SlidleGameStatus,
  moves: number,
}

function ImageTile(props: {
  disabled: boolean,
  onClick: () => void,
  tileSize: number,
  tile: HTMLCanvasElement | null
}): ReactElement {
  return <ButtonBase
    disabled={props.disabled}
    onClick={props.onClick}
    style={{padding: 0}}>
    <div style={{width: props.tileSize, height: props.tileSize}}>
      {props.tile
        ? <img src={props.tile.toDataURL("image/png")}
               style={{verticalAlign: "top", width: "100%", height: "100%", objectFit: "cover"}}/>
        : null}
    </div>
  </ButtonBase>
}

export class SlidleGameFragment extends GameFragment<SlidleGameFragmentProps, SlidleGameConfig, SlidleGameData, SlidleGameFragmentState> {

  private board: SlidleGameBoard;

  protected getGame(): Game {
    return GAME_SLIDLE;
  }

  private readonly keyListener = (ev: KeyboardEvent) => {
    const complete = this.state.gameStatus === SlidleGameStatus.STATUS_COMPLETE;
    if (complete) {
      return;
    }
    switch (ev.code) {
      default:
        return;
      case "ArrowUp":
        this.moveTile("Up");
        break;
      case "ArrowDown":
        this.moveTile("Down");
        break;
      case "ArrowLeft":
        this.moveTile("Left");
        break;
      case "ArrowRight":
        this.moveTile("Right");
        break;
    }
    ev.preventDefault();
  };

  private touchStartX: number;
  private touchStartY: number;

  private readonly touchStartListener = (ev: TouchEvent) => {
    const complete = this.state.gameStatus === SlidleGameStatus.STATUS_COMPLETE;
    if (complete) {
      return;
    }
    this.touchStartX = ev.changedTouches[0].clientX;
    this.touchStartY = ev.changedTouches[0].clientY;
  };

  private readonly touchEndListener = (ev: TouchEvent) => {
    const complete = this.state.gameStatus === SlidleGameStatus.STATUS_COMPLETE;
    if (complete) {
      return;
    }
    const dx = ev.changedTouches[0].clientX - this.touchStartX;
    const dy = ev.changedTouches[0].clientY - this.touchStartY;
    this.touchStartX = this.touchStartY = undefined;
    if (Math.sqrt(dx * dx + dy * dy) < 8) {
      return;
    }
    if (Math.abs(dx) < Math.abs(dy)) {
      if (dy < 0) {
        this.moveTile("Up");
      } else {
        this.moveTile("Down");
      }
    } else {
      if (dx < 0) {
        this.moveTile("Left");
      } else {
        this.moveTile("Right");
      }
    }
  };

  private readonly throttler = new Throttler(100 * 1.5);

  private moveTile(action: "Up" | "Down" | "Left" | "Right") {
    this.throttler.run(() => this.moveTileInternal(action));
  }

  private moveTileInternal(action: "Up" | "Down" | "Left" | "Right") {
    const board = this.board;
    if (!board) {
      return null;
    }
    const tilesCount = board.level.tilesCount;
    let n: number;
    if ((n = validMoveIndex(tilesCount, board.tileIndices, action)) >= 0) {
      this.makeMove(n);
    }
  }

  private makeMove(index: number): void {
    const board = this.board;
    const emptyIndex = findEmptyIndex(board.level.tilesCount, board.tileIndices);
    const t = board.tileIndices[index];
    board.tileIndices[index] = board.tileIndices[emptyIndex];
    board.tileIndices[emptyIndex] = t;
    this.setState({
      moves: this.state.moves + 1,
    });
  }

  protected onCreateState(): SlidleGameFragmentState {
    return {
      ...super.onCreateState(),
      gameStatus: SlidleGameStatus.STATUS_IN_PROGRESS,
      moves: 0,
    };
  }

  componentDidMount() {
    super.componentDidMount();
    window.addEventListener("keydown", this.keyListener);
    window.addEventListener("touchstart", this.touchStartListener);
    window.addEventListener("touchend", this.touchEndListener);
  }

  componentWillUnmount() {
    super.componentWillUnmount();
    window.removeEventListener("keydown", this.keyListener);
    window.removeEventListener("touchstart", this.touchStartListener);
    window.removeEventListener("touchend", this.touchEndListener);
  }

  componentDidUpdate(prevProps: Readonly<SlidleGameFragmentProps>, prevState: Readonly<SlidleGameFragmentState>, snapshot?: any) {
    super.componentDidUpdate(prevProps, prevState, snapshot);
    if (prevState.gameData !== this.state.gameData) {
      this.board = this.state.gameData.gamePlay.jsonData as SlidleGameBoard;
    }
    const moves = this.state.moves;
    if (prevState.moves !== moves) {
      const board = this.board;
      const tilesCount = board.level.tilesCount;
      const totalTiles = tilesCount * tilesCount;
      let complete: boolean = true;
      for (let i = 0; i < totalTiles; i++) {
        if (board.tileIndices[i] !== i) {
          complete = false;
          break;
        }
      }
      if (complete) {
        this.props.gameFragmentListener?.onStatusChanged("Game complete.");
        let resultText: string = "You completed %{level} game.";
        switch (this.state.gameConfig.level) {
          case SlidleDifficultyLevel.EASY:
            resultText = StringUtil.format(resultText, $KTS("level", "an easy"));
            break;
          case SlidleDifficultyLevel.MEDIUM:
            resultText = StringUtil.format(resultText, $KTS("level", "a medium"));
            break;
          case SlidleDifficultyLevel.HARD:
            resultText = StringUtil.format(resultText, $KTS("level", "a hard"));
            break;
          case SlidleDifficultyLevel.VERY_HARD:
            resultText = StringUtil.format(resultText, $KTS("level", "a very hard"));
            break;
        }
        resultText = StringUtil.format(resultText, $KTS("moves", moves.toString()));
        this.finishGame(new GameOverResult(GAME_SLIDLE.id, GameOverResultType.WON, this.board.level.name, this.board.getPoints(moves), 0, resultText, false));
        this.setState({
          gameStatus: SlidleGameStatus.STATUS_COMPLETE,
        });
      }
    }
  }

  protected async fetchOnMount(forceReload?: boolean): Promise<void> {
    const gameConfig = this.state.gameConfig;
    if (!gameConfig) {
      return;
    }
    this.setState({
      gameData: await SlidleGameData.create(gameConfig),
    });
  }

  protected renderConfigContent(): React.ReactElement {
    return this.renderConfigContentForGame(GAME_SLIDLE, <>
      {SlidleDifficultyLevel.VALUES.map(level =>
        <Button variant="contained"
                onClick={() => this.setState({gameConfig: new SlidleGameConfig(level)})}>{level.text}</Button>)}
    </>);
  }

  protected renderToolbarTitle(): React.ReactElement {
    return <Typography
      style={{marginLeft: PD_MD,}}>{(this.state.gameData.gamePlay.jsonData as SlidleGameBoard).level.text}</Typography>
  }

  protected renderToolbarButtons() {
    return <>
      <Button onClick={() => BaseApp.CONTEXT.showDialog({flags: DIALOG_FLAG_SHOW_CLOSE}, props => {
        return <StyledBoxColumn style={{padding: PD_LG, maxWidth: DW_SM, alignItems: "center", gap: PD_LG}}>
          <Typography variant="h5">How to play Slidle</Typography>
          <Typography>Slidle is a combination of the traditional 15-puzzle sliding game and a jigsaw puzzle. Move the
            tiles into the empty space to re-create the original image.
          </Typography>
        </StyledBoxColumn>;
      })}>
        <HelpOutlineOutlined/>
      </Button>
      <Button onClick={(event) => BaseApp.CONTEXT.showActions(event.target as HTMLButtonElement, null, [
        new Action("Show original", () => {
          const id = BaseApp.CONTEXT.showDialog(null, () => <StyledBoxColumn
            style={{width: "84dwh", maxWidth: DW_SM, alignItems: "center", gap: PD_LG, padding: PD_LG}}>
            <img src={this.board.layout.original.toDataURL()} style={{width: "100%", aspectRatio: 1}}/>
            <Button onClick={() => BaseApp.CONTEXT.hideDialog(id)} variant="contained">Close</Button>
          </StyledBoxColumn>)
        }),
      ])}>
        <MoreHorizOutlined/>
      </Button>
    </>
  }

  protected renderGameContent(): ReactElement | null {
    const board = this.board;
    if (!board) {
      return null;
    }
    const tilesCount = board.level.tilesCount;
    const totalTiles = tilesCount * tilesCount;
    const tileSize = 640 / tilesCount;
    const zoom = Math.min(1, (window.innerWidth - PD_LG * 2) / (tileSize * tilesCount));
    const rows: HTMLCanvasElement[][] = [];
    const complete = this.state.gameStatus === SlidleGameStatus.STATUS_COMPLETE;
    for (let i = 0; i < tilesCount; i++) {
      rows.push(board.tileIndices.slice(i * tilesCount, (i + 1) * tilesCount).map(tileIndex => (complete || tileIndex !== (totalTiles - 1)) ? board.layout.tiles[tileIndex] : null));
    }
    const swapIndices = findSwapIndices(tilesCount, board.tileIndices);
    return <StyledBoxColumn style={{flexGrow: 1, width: "100%", maxWidth: 480, margin: "auto", alignItems: "center"}}>
      <StyledBoxColumn style={{gap: 4, zoom: zoom, border: DIVIDER_XXBLACK, background: "black"}}>
        {rows.map((row, rowIndex) => <StyledBoxRow style={{gap: 4}}>{row.map((tile, colIndex) => {
          const index = rowIndex * tilesCount + colIndex;
          return <ImageTile
            onClick={() => this.makeMove(index)}
            disabled={!swapIndices.includes(index)}
            tileSize={tileSize}
            tile={tile}/>;
        })}</StyledBoxRow>)}
      </StyledBoxColumn>
    </StyledBoxColumn>;
  }
}